import { client } from '@/client';
import {
  PhishingSign,
  PhishingSignsListResponse,
  SavePhishingSignRequest,
} from '@/client/campaigns';
import {
  DeletePhishingSignMutation,
  GenericForbiddenApiError,
  SavePhishingSignMutation,
  UpdatePhishingSignMutation,
} from '@/hooks/query';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const listQueryKey = 'phishingSigns';
const singleQueryKey = 'phishingSign';

export const usePhishingSigns = (
  params: UseQueryOptions = {},
): UseQueryResult<PhishingSignsListResponse> & {
  phishingSigns: PhishingSignsListResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [listQueryKey],
    () => client.phishingSigns.getPhishingSigns(),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    phishingSigns: data,
    ...(rest as UseQueryResult<PhishingSignsListResponse>),
  };
};

export const usePhishingSign = (
  params: UseQueryOptions & { id: string },
): UseQueryResult<PhishingSign> & {
  phishingSign: PhishingSign | undefined;
} => {
  const {
    id,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [singleQueryKey, id],
    () => client.phishingSigns.getPhishingSign(id),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!id,
      ...(restOptions as any),
    },
  );

  return {
    phishingSign: data,
    ...(rest as UseQueryResult<PhishingSign>),
  };
};

export const useCreatePhishingSign = (): {
  create: (payload: SavePhishingSignRequest) => Promise<PhishingSign>;
} & SavePhishingSignMutation => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    PhishingSign,
    GenericForbiddenApiError,
    SavePhishingSignRequest
  >(
    'createPhishingSign',
    (payload) => client.phishingSigns.createPhishingSign(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([listQueryKey]);
      },
    },
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdatePhishingSign = (): {
  update: (
    payload: { id: string } & SavePhishingSignRequest,
  ) => Promise<PhishingSign>;
} & UpdatePhishingSignMutation => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    PhishingSign,
    GenericForbiddenApiError,
    { id: string } & SavePhishingSignRequest
  >(
    'updatePhishingSign',
    (payload) => {
      const { id, ...request } = payload;
      return client.phishingSigns.updatePhishingSign(id, request);
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([listQueryKey]);
        queryClient.invalidateQueries([singleQueryKey, variables.id]);
      },
    },
  );

  return { update: mutateAsync, ...(rest as any) };
};

export const useDeletePhishingSign = (): {
  delete: (id: string) => Promise<void>;
} & DeletePhishingSignMutation => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    string
  >('deletePhishingSign', (id) => client.phishingSigns.deletePhishingSign(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([listQueryKey]);
    },
  });

  return { delete: mutateAsync, ...(rest as any) };
};
